import { floor, get, isArray, isNull, isUndefined, max } from "lodash";

import { IChartFormValue, StatisticsType } from "../types/form-type";
import { ColumnType, ITableData, IWrokspaceData, NumberFormat } from "../types/table-type";
import { IDatasetSource, IDatasetTransform, ITooltipFormatterParams } from "../types/echarts-type";
import { ChartErrorType } from "../types/global-type";

import { getQuery } from "../utils/app";
import { getCalculationColumn, getLabelsWidth, getLabelWidth, checkGroupId } from "../utils/option_secondary";
import CalculationDataByChart from "./CalculationDataByChart";
import { checkColumnHasColor, getRealColumn } from "../utils/option_main";

import { FONT_FAMILY } from "../constants/app";
import { LEGEND_ITEM_WIDTH, PIE_CHART_NOT_SHOW_LABEL, IS_PIE_CHART, IS_BAR_CHART } from "../constants/chart";
import {
  DIMENSION_COLUMN_TYPE,
  VALUE_COLUMN_TYPE,
  TABLE_CELL_VALUE_ICON_PENTAGRAM,
  HAS_COLOR_BY_OPTION,
} from "../constants/tableData";

export interface IChartProps {
  tableData: ITableData;
  workspaceData: IWrokspaceData;
  formValue: IChartFormValue;
}

/**
 * chart 基础功能 class
 * 这是一个抽象类 定义了一个 chart class 需要有 getXAxis getTooltip 等方法 这些方法返回值是 echarts 的 xAxis tooltip 配置项
 * 同时有一个 getOption 方法 用于调用 getXAxis getTooltip 等方法 组装成给 echarts 的 option 同时负责做一些校验
 * 由于不同类型的 chart getXAxis getTooltip 等方法实现不同 所以这里定义为 abstract
 */
abstract class Chart {
  public tableData: ITableData;
  public workspaceData: IWrokspaceData;
  public formValue: IChartFormValue;

  public stackTypeList: string[];

  public datasetObj: (IDatasetSource | IDatasetTransform)[];

  // canvas 尺寸
  private canvasWidth: number;
  private canvasHeight: number;

  private chartError: ChartErrorType | null;

  public isExpand: boolean;

  public calculation: any;
  public formatValue: (data: number) => string;
  public formatDimension: (data: any) => string;
  public formatGroup: (data: any) => string;
  //  环图 内圆几何属性 圆心坐标 & 半径
  public innerCircleGeometricProperties?: { center: [number, number]; radius: number };
  //  环图 中心展示内容
  public centerValue?: string;

  constructor(props: IChartProps) {
    const { tableData, workspaceData, formValue } = props;
    this.tableData = tableData;
    this.workspaceData = workspaceData;
    this.formValue = formValue;
    this.stackTypeList = [];
    this.datasetObj = [];
    this.canvasWidth = 0;
    this.canvasHeight = 0;
    this.chartError = null;

    this.isExpand = getQuery().isExpand;

    const calculation = new CalculationDataByChart({ tableData, formValue });
    this.calculation = calculation;
    const employGlobalFormat = calculation.checkGlobalFormat();
    this.formatValue = calculation.formatValue;
    this.formatDimension = (value: string) =>
      employGlobalFormat ? this.calculation.formatByIndex.get(value) : calculation.formatDimension(value);
    this.formatGroup = (value: string) => this.calculation.formatByIndex.get(value);

    /**
     * 当 两个维度都选择 dateTime 且开启 时间粒度分组 时 xAxis label 会使用 legend 的 format
     * 因为 basicData 中 只有一个 columnId 的 key 循环 keyList 时 formatByIndex 会 set 两个一样的 key
     * 第一个 value 是 时间粒度分组的 format 第二个 value 是 图例的 format
     */
    const groupId = checkGroupId(this.formValue, this.tableData, this.formValue.chartType);
    if (
      this.formValue.dimensionId === groupId &&
      IS_BAR_CHART[this.formValue.chartType] &&
      this.formValue.isGroupAtCycle
    ) {
      this.formatDimension = (value: string) => calculation.formatDimension(value);
    }
  }

  public abstract getXAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): {};

  public abstract getYAxis(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): {};

  public abstract getTooltip(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): {};

  public abstract getLegend(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): {};

  public abstract getVisualMap(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData
  ): {} | null;

  public abstract getDataset(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData,
    calculation: CalculationDataByChart
  ): {};

  public abstract getGrid(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): {};

  public abstract getSeries(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData): any;

  public abstract checkChartOptionError(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData
  ): ChartErrorType | null;

  // 额外的计算函数 在计算完 dataset 后执行 用于根据 dataset 计算其他属性需要的内容
  public abstract calculationOther(
    dataset: (IDatasetSource | IDatasetTransform)[],
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData
  ): void;

  public getOption() {
    // 表单值校验
    const checkColumnFlag = this.checkColumnId();
    if (checkColumnFlag) {
      this.setChartError(checkColumnFlag);
      return;
    }

    this.formValue = this.checkFormValue(this.formValue);

    const dataset = this.getDataset(this.formValue, this.tableData, this.workspaceData, this.calculation);
    this.datasetObj = dataset as (IDatasetSource | IDatasetTransform)[];
    this.calculationOther(this.datasetObj, this.formValue, this.tableData, this.workspaceData);
    this.setChartError(this.checkChartOptionError(this.formValue, this.tableData, this.workspaceData));

    // 统一校验是否有可以用于绘制的数据
    const source = (this.datasetObj[0] as IDatasetSource).source;
    if (source.length === 0) {
      this.setChartError(ChartErrorType.DATASET_IS_EMPTY_ARRAY);
      return;
    }

    const option = {
      dataset: dataset,
      legend: this.getLegend(this.formValue, this.tableData, this.workspaceData),
      tooltip: this.getTooltip(this.formValue, this.tableData, this.workspaceData),
      xAxis: this.getXAxis(this.formValue, this.tableData, this.workspaceData),
      yAxis: this.getYAxis(this.formValue, this.tableData, this.workspaceData),
      series: this.getSeries(this.formValue, this.tableData, this.workspaceData),
      grid: this.getGrid(this.formValue, this.tableData, this.workspaceData),
      visualMap: this.getVisualMap(this.formValue, this.tableData, this.workspaceData),

      // 全局字体设置
      textStyle: {
        fontFamily: FONT_FAMILY,
      },
    };

    return option;
  }

  public getCanvasWidth() {
    return this.canvasWidth;
  }

  public setCanvasWidth(value: number) {
    this.canvasWidth = value;
  }

  public getCanvasHeight() {
    return this.canvasHeight;
  }

  public setCanvasHeight(value: number) {
    this.canvasHeight = value;
  }

  public setChartError(err: ChartErrorType | null) {
    this.chartError = err;
  }

  public getChartError() {
    return this.chartError;
  }

  public getYAxisName() {
    const { statisticsType, calculationTargetId } = this.formValue;
    const column = getCalculationColumn(this.tableData, statisticsType, calculationTargetId);
    if (!column) return "";
    const { type, typeOptions } = column;
    switch (type) {
      case ColumnType.NUMBER:
        const name = get(typeOptions, "numberFormat") === NumberFormat.PERCENTAGE ? "%" : "";
        return name;
      case ColumnType.CURRENCY:
        return get(typeOptions, "currencyFormat");
      case ColumnType.RATING:
        return TABLE_CELL_VALUE_ICON_PENTAGRAM;
      default:
        return "";
    }
  }

  // 如果这是 函数而非方法 则子 class 的对想在调用时 this 为 undefined 毕竟 classChart 没有实例化  考虑来个 init 方法 用于挂载数据
  public formatYAxisLabel = (value: number) => {
    const { statisticsType, calculationTargetId } = this.formValue;
    const column = getCalculationColumn(this.tableData, statisticsType, calculationTargetId);
    if (!column) return value;
    if (column.type === ColumnType.NUMBER) {
      const numberFormat = get(column.typeOptions, "numberFormat");
      if (numberFormat === NumberFormat.PERCENTAGE) {
        return value * 100;
      }
    }
    return value;
  };

  public getLegendContainerWidth() {
    return this.isExpand ? this.canvasWidth - 64 : this.canvasWidth - 48;
  }

  public convertTooltipParams(
    params: ITooltipFormatterParams[] | ITooltipFormatterParams,
    isFilterGroupUudefined?: boolean
  ): ITooltipFormatterParams[] {
    const list = isArray(params) ? params : [params];
    let result = list.map((item: ITooltipFormatterParams) => {
      const newItem = {
        ...item,
        data: [
          this.formatDimension(item.data[0]),
          this.formatValue(item.data[1] as number),
          this.formatGroup(item.data[2]),
        ],
      };
      return newItem;
    });
    if (isFilterGroupUudefined) {
      result = result.filter((item) => !isUndefined(item.data[1]) && !isNull(item.data[1]) && item.data[1] !== "");
    }
    return result;
  }

  public getLegendItemWidth() {
    const transformList = this.datasetObj.filter(
      (item) => !!get(item, "transform") && get(item, "transform.type") === "filter"
    );
    const legendCount = transformList.length;
    if (legendCount < 2) return null;
    const legendNameList = transformList.map((item) => get(item, "transform.config.value")).map(this.formatGroup);
    const legendNameWidthList = getLabelsWidth(legendNameList);
    const maxWidth = max(legendNameWidthList) || legendNameWidthList[0];
    if (maxWidth < LEGEND_ITEM_WIDTH) return null;
    return LEGEND_ITEM_WIDTH;
  }

  // 校验 列id 的有效性
  public checkColumnId(): ChartErrorType | null {
    const { dimensionId, statisticsType, calculationTargetId } = this.formValue;
    if (!dimensionId) return ChartErrorType.SOME_FIELDS_ARE_UNDEFINED;
    const dimensionColumn = getRealColumn(dimensionId, this.tableData);
    if (!dimensionColumn) return ChartErrorType.SOME_FIELDS_ARE_INVALID;
    if (!DIMENSION_COLUMN_TYPE[dimensionColumn.type]) return ChartErrorType.SOME_FIELDS_ARE_INVALID;
    if (statisticsType === StatisticsType.STATISTIC_COLUMN) {
      if (!calculationTargetId) return ChartErrorType.SOME_FIELDS_ARE_UNDEFINED;
      const calculationColumn = getRealColumn(calculationTargetId, this.tableData);
      if (!calculationColumn) return ChartErrorType.SOME_FIELDS_ARE_INVALID;
      if (!VALUE_COLUMN_TYPE[calculationColumn.type]) return ChartErrorType.SOME_FIELDS_ARE_INVALID;
    }
    return null;
  }

  public basicFormatLegend(name: any): string {
    const realName = this.formatGroup(name);
    const realNameWidth = getLabelWidth(realName);
    if (realNameWidth <= LEGEND_ITEM_WIDTH) return realName;
    const cutIndex = floor((realName.length * LEGEND_ITEM_WIDTH) / realNameWidth);
    return `${realName.slice(0, cutIndex - 1)}...`;
  }

  public checkFormValue(value: IChartFormValue): IChartFormValue {
    const { chartType } = value;
    const newValue = { ...value };
    if (IS_PIE_CHART[chartType]) {
      if (Math.min(this.getCanvasWidth(), this.getCanvasHeight()) < PIE_CHART_NOT_SHOW_LABEL) {
        newValue.isShowLabel = false;
      }
    }
    return newValue;
  }

  public checkDimensionColumnHasColor(): boolean {
    return checkColumnHasColor({ columnId: this.formValue.dimensionId, tableData: this.tableData });
  }

  public checkGroupColumnHasColor(): boolean {
    const groupId = checkGroupId(this.formValue, this.tableData, this.formValue.chartType);
    return checkColumnHasColor({ columnId: groupId, tableData: this.tableData });
  }
}

export default Chart;
